import React from 'react';
import { Link } from 'react-router-dom';
import { SparklesIcon, HeartIcon, ZapIcon, ArrowRightIcon } from 'lucide-react';
import { MoodCluster, getNodeById } from '../../../data/storyGalaxyData';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { useRoleTheme } from '../../../hooks/useRoleTheme';
interface MoodClusterCardProps {
  cluster: MoodCluster;
}
const themeMap = {
  'violet-cosmic': {
    from: 'from-violet-900',
    to: 'to-purple-900',
    isDark: true,
    icon: SparklesIcon
  },
  'peach-gold': {
    from: 'from-amber-100',
    to: 'to-orange-200',
    isDark: false,
    icon: HeartIcon
  },
  'electric-blue': {
    from: 'from-blue-900',
    to: 'to-cyan-800',
    isDark: true,
    icon: ZapIcon
  }
};
export const MoodClusterCard: React.FC<MoodClusterCardProps> = ({
  cluster
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const theme = themeMap[cluster.visualTheme] || themeMap['violet-cosmic'];
  const Icon = theme.icon;
  const isDarkTheme = theme.isDark;
  // Resolve featured nodes
  const featuredNodes = cluster.featuredNodeIds.map(getNodeById).filter(Boolean);
  return <Link to={`/story-galaxy/${cluster.id}`} className={`group relative overflow-hidden rounded-2xl border ${dk.border} shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-1 flex flex-col h-full`} data-id="element-14087" data-mp="isi3cw5m4s6h">
      {/* Background Gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${theme.from} ${theme.to} opacity-90 group-hover:opacity-100 transition-opacity duration-300`} data-id="element-14088" data-mp="4sz7b1q2t7aj" />

      {/* Star/Particle Overlay (Subtle) */}
      <div className={`absolute inset-0 opacity-20 mix-blend-overlay`} style={{
      backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
      backgroundSize: '24px 24px'
    }} data-id="element-14089" data-mp="3r81thmazluhb" />

      <div className={`relative p-6 sm:p-8 flex flex-col h-full ${isDarkTheme ? 'text-white' : 'text-slate-900'}`} data-id="element-14090" data-mp="3d77jiz8ajlgj">
        <div className="flex items-center justify-between mb-4" data-id="element-14091" data-mp="33jc27349tngn">
          <div className={`p-3 rounded-xl backdrop-blur-md ${isDarkTheme ? 'bg-white/10' : 'bg-black/5'}`} data-id="element-14092" data-mp="37m8bu4bu298g">
            <Icon className="h-6 w-6" data-id="element-14093" data-mp="2tn8yhstrtgrl" />
          </div>
          <div className={`opacity-0 group-hover:opacity-100 transition-opacity transform translate-x-2 group-hover:translate-x-0 duration-300`} data-id="element-14094" data-mp="2yksd4tudrvk2">
            <ArrowRightIcon className="h-5 w-5" data-id="element-14095" data-mp="3mcppcm1qsplp" />
          </div>
        </div>

        <h3 className="text-2xl font-bold mb-2 tracking-tight" data-id="element-14096" data-mp="3r96bxbtamvwo">
          {cluster.name}
        </h3>

        <p className={`text-sm mb-6 flex-grow ${isDarkTheme ? 'text-white/80' : 'text-slate-700'}`} data-id="element-14097" data-mp="3cka5tkasrsyp">
          {cluster.shortDescription}
        </p>

        <div className="mt-auto" data-id="element-14098" data-mp="93t9fifydagj">
          <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkTheme ? 'text-white/60' : 'text-slate-500'}`} data-id="element-14099" data-mp="92rz5g6rgrzv">
            Constellation Includes
          </p>
          <div className="flex flex-wrap gap-2" data-id="element-14100" data-mp="1yjduvr9xr6nh">
            {featuredNodes.slice(0, 3).map(node => <span key={node!.id} className={`text-xs px-2.5 py-1 ${rt.badgeRadius} backdrop-blur-md font-medium border ${isDarkTheme ? 'bg-white/10 border-white/20 text-white/90' : 'bg-black/5 border-black/10 text-slate-800'}`} data-id="element-14101" data-mp="1tk15ayrojlh7">
                {node!.name}
              </span>)}
            {featuredNodes.length > 3 && <span className={`text-xs px-2.5 py-1 ${rt.badgeRadius} backdrop-blur-md font-medium border ${isDarkTheme ? 'bg-white/5 border-white/10 text-white/60' : 'bg-black/5 border-black/10 text-slate-600'}`} data-id="element-14102" data-mp="1ap41ho4n469u">
                +{featuredNodes.length - 3}
              </span>}
          </div>
        </div>
      </div>
    </Link>;
};